import { Link, useLocation } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'

const navItems = [
  { path: '/', icon: 'dashboard', label: 'Dashboard' },
  { path: '/quick-start', icon: 'rocket_launch', label: 'Quick Start Guide' },
  { path: '/projects', icon: 'folder_open', label: 'My Projects' },
  { path: '/gallery', icon: 'photo_library', label: 'Gallery' },
  { path: '/styles', icon: 'style', label: 'Style References' },
  { path: '/subscription', icon: 'card_membership', label: 'Subscription' },
  { path: '/settings', icon: 'settings', label: 'Settings' },
]

interface MobileSidebarProps {
  isOpen: boolean
  onClose: () => void
}

export function MobileSidebar({ isOpen, onClose }: MobileSidebarProps) {
  const location = useLocation()
  const { user, signOut } = useAuthStore()

  if (!isOpen) return null

  // Use availableCredits for display (subscription + bonus - reserved)
  const availableCredits = user?.availableCredits ?? user?.credits ?? 0
  const maxCredits = user?.maxCredits ?? (user?.monthlyCredits ?? 10) * 3
  const creditsPercentage = user
    ? Math.round((availableCredits / maxCredits) * 100)
    : 0

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40 md:hidden"
        onClick={onClose}
      />

      {/* Sidebar */}
      <div className="fixed inset-y-0 left-0 w-72 bg-surface z-50 md:hidden flex flex-col justify-between p-6 shadow-sm">
        <div className="flex flex-col gap-8">
          {/* Header */}
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3" onClick={onClose}>
              <img src="/logo.png" alt="Kalypso AI" className="size-10 rounded-xl object-cover" />
              <span className="text-xl font-bold tracking-tight text-primary-dark">
                Kalypso AI
              </span>
            </Link>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-surface-soft text-text-main"
            >
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex flex-col gap-1.5">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={onClose}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-xl transition-colors relative
                    ${isActive
                      ? 'bg-accent/50 text-primary-dark font-medium'
                      : 'text-text-muted hover:bg-surface-soft hover:text-text-main'
                    }
                  `}
                >
                  <span className={`material-symbols-outlined ${isActive ? 'filled' : ''}`}>
                    {item.icon}
                  </span>
                  <span className="text-sm">{item.label}</span>
                </Link>
              )
            })}
          </nav>
        </div>

        <div className="flex flex-col gap-6">
          {/* Credits */}
          <div className="bg-surface-soft p-5 rounded-xl flex flex-col gap-3 border border-border-color">
            <div className="flex justify-between items-center text-xs font-semibold text-text-muted uppercase tracking-wider">
              <span>Available Credits</span>
              <span className="text-primary-dark">{availableCredits}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(creditsPercentage, 100)}%` }}
              />
            </div>
            {user?.bonusCredits && user.bonusCredits > 0 && (
              <div className="text-[10px] text-amber-600 flex items-center gap-1">
                <span className="material-symbols-outlined text-xs">shopping_bag</span>
                Includes {user.bonusCredits} from packs
              </div>
            )}
          </div>

          {/* User & Sign Out */}
          <div className="flex items-center justify-between px-2 pt-2 border-t border-border-color">
            <div className="flex items-center gap-3">
              {user?.avatarUrl ? (
                <div
                  className="bg-center bg-no-repeat bg-cover rounded-full size-10 border-2 border-white shadow-sm"
                  style={{ backgroundImage: `url("${user.avatarUrl}")` }}
                />
              ) : (
                <div className="size-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="material-symbols-outlined text-primary">person</span>
                </div>
              )}
              <div className="flex flex-col">
                <p className="text-sm font-bold text-text-main">{user?.name || 'User'}</p>
                <p className="text-xs text-text-muted">{user?.subscriptionTier || 'Free'} Plan</p>
              </div>
            </div>
            <button
              onClick={() => {
                signOut()
                onClose()
              }}
              className="p-2 rounded-lg hover:bg-surface-soft text-text-muted"
            >
              <span className="material-symbols-outlined">logout</span>
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
